import qs from 'querystring-es3'
import { ICreateBoatTaskForm } from './components/Dialogs/BoatRiskAnalysis/BoatRiskAnalaysisModel'
import { BoatFormType } from './components/Forms/boat/BoatFormContainer'
import { BoatExerciseForm } from './components/Forms/BoatExerciseForm'
import { ExerciseFormType } from './components/Forms/ExerciseForm'
import { INotification } from './components/Notifications/NotificationsModel'
import { BoatCounterType, RiskAnalysisRiskStatus, ServiceRepeatType, WorkshopBoatStatus } from './enums'
import { authHeader, getShareTokenAuthHeader } from './helpers/authHeader'
import { axios } from './helpers/axios'
import { history } from './helpers/history'
import userManager from './helpers/UserManager'
import {
  BoatCardInfo,
  BoatCounterInfo,
  BoatCounterLogServicesMinimum,
  BoatRoutineContainer,
  BoatShutdownRoutine,
  BoatStartupRoutine,
  BoatUser,
  Checklist,
  CheckListCompletion,
  ChecklistItem,
  ClientSettingType,
  CreateStartupRoutineReq,
  CrewCheckin,
  DailyCheckin,
  Defibrillator,
  Deviation,
  DeviationStatus,
  DocumentCategory,
  Equipment,
  FamiliarizationForms,
  FamiliarizationUserLink,
  IWorkcaseAssignmentRequest,
  LocalityFromHavbruksloggen,
  MainCategory,
  StartupRoutineDaily,
  StartupRoutineHistoryMinimal,
  Stock,
  Supplier,
} from './Http/DataTypes'
import { requestHeader } from './store/ServicesHelper'
import { Role } from './types/authorization-types'
import { ICalendarEvent } from './types/calendar-types'
import {
  ClientAndBoatDeadlines,
  IAudit,
  IBoat,
  IBoatDocument,
  IBoatListResult,
  IBoatOverviewDto,
  IBoatRiskAnalysisGeneralInfo,
  IBoatRiskAnalysisSubTask,
  IBoatRiskAnalysisTask,
  IBoatStock,
  IChecklist,
  IClientMinimal,
  IClientUserLink,
  IExercise,
  IExerciseOccurence,
  IFile,
  IFileFolder,
  IGetDbOccurenceResponse,
  IGetWorkshopReponse,
  ILubricantMap,
  IOccurenceOverview,
  IProduct,
  IProductCategory,
  IRiskAnalysisSubTask,
  IRiskAnalysisTask,
  IStockUser,
  ISubTaskDanger,
  ISubTaskMeasure,
  IUser,
  IUserEmailCategory,
  IUserExtended,
  IUserSession,
  IWorkCasesPaginatedResponse,
  IWorkOrder,
  IWorkOrderPaginatedResponse,
  IWorkOrderPreview,
  IWorkshop,
  IWorkshopBoat,
  IWorkshopBoatResponse,
  IWorkshopBoatStatusResponse,
  IWorkshopOverview,
  IWorkshopUser,
  IWorkshopUserDetails,
  IWorkshopWorkOrders,
  IWorkshopWorkOrderSectionResponse,
  IWorkshopWorkOrderStatus,
  WorkshopUserNotificationPreference,
} from './types/data-types'

export async function getMe(key) {
  const { data } = await axios.get<IUserExtended>('api/auth/me')
  axios.defaults.headers['TokenId'] = data.tokenId
  axios.defaults.headers['Role'] = data.userRole
  axios.defaults.headers['ClientPermission'] = data.encryptedPermissionValue
  return data
}

export async function refreshAccessToken() {
  const user = await userManager.renewToken()
  return user
}

export async function removeLoginSession(sessionId: number) {
  const { data } = await axios.post(`api/auth/remove-access/${sessionId}`)

  return data
}

export async function removeLoginSessionByToken(refreshToken: string) {
  const { data } = await axios.post(`api/auth/remove-access/${refreshToken}`)

  return data
}

export async function getStocks(key, params) {
  const { data } = await axios.get('/api/stocks', { params })

  return data
}

// gets the stock products available for a specific boat
export async function getStockProductsForBoat(_key, boatId: number): Promise<Stock[]> {
  const { data } = await axios.get(`/api/stocks/stock-products/${boatId}`)
  return data
}

export async function getStocksByClient(key, clientId = null) {
  const url = clientId ? `/api/stocks/by-client/${clientId}` : '/api/stocks/by-client/'
  const { data } = await axios.get(url)

  return data
}

export async function getAllClientSpecificStocks(key, stockId: number) {
  const url = `/api/stocks/${stockId}/all-stocks`
  const { data } = await axios.get(url)

  return data
}
export async function getProductCount(stockId: number, productId: number) {
  const url = `/api/stocks/${stockId}/products/${productId}`
  const { data } = await axios.get(url)

  return data
}
export async function getStock(key, id) {
  const { data } = await axios.get(`/api/stocks/${id}`)
  return data
}

export async function getBoatStock(boatId: number) {
  const { data } = await axios.get<IBoatStock>(`/api/boats/${boatId}/stock`)
  return data
}

export async function createStock(form) {
  if (!form.boatId) form.boatId = undefined

  const { data } = await axios.post('/api/stocks', form)

  return data
}

export async function updateStock({ id, form }) {
  if (!form.boatId) form.boatId = undefined

  const { data } = await axios.put(`/api/stocks/${id}`, form)

  return data
}

export async function deleteStock(id) {
  const { data } = await axios.delete(`/api/stocks/${id}`)

  return data
}

export async function getStockProducts(key, id, params) {
  const { data } = await axios.get(`/api/stocks/${id}/products`, { params })

  return data
}
export async function getStockEquipment(key, id, params) {
  const { data } = await axios.get(`/api/stocks/${id}/equipment`, { params })

  return data
}

export async function createStockProduct({ stockId, form }) {
  const { data } = await axios.post(`/api/stocks/${stockId}/products`, form)

  return data
}

export async function updateStockProduct({ stockId, productId, form }) {
  const { data } = await axios.put(`/api/stocks/${stockId}/products/${productId}`, form)

  return data
}

export async function deleteStockProduct({ stockId, id }) {
  const { data } = await axios.delete(`/api/stocks/${stockId}/products/${id}`)

  return data
}

export async function getStockProductTransactions(key, stockId, productId) {
  const { data } = await axios.get(`/api/stocks/${stockId}/products/${productId}/transactions`)

  return data
}

export async function createStockProductTransaction({ stockId, productId, form }) {
  const { data } = await axios.post(`/api/stocks/${stockId}/products/${productId}/transactions`, form)

  return data
}

export async function deleteStockProductTransaction({ stockId, transaction }) {
  const { stockProductId, stockProductTransactionId: id } = transaction

  const { data } = await axios.delete(`/api/stocks/${stockId}/products/${stockProductId}/transactions/${id}`)

  return data
}

export async function getProducts(key, clientId) {
  const { data } = await axios.get('/api/products', {
    headers: {
      'X-Client-Id': clientId,
    },
  })

  return data
}

export async function searchProducts(params) {
  const { data } = await axios.get('/api/products/search', { params })

  return data
}

export async function getProduct(key, id) {
  const { data } = await axios.get(`/api/products/${id}`)

  return data
}

export async function createProduct(form) {
  const { data } = await axios.post('/api/products', form)

  return data
}

export async function updateProduct({ id, form }) {
  const { data } = await axios.put(`/api/products/${id}`, form)

  return data
}

export async function deleteProduct(id) {
  const { data } = await axios.delete(`/api/products/${id}`)

  return data
}

export async function getBoats() {
  const { data } = await axios.get('/api/Boats/AllBoatsForClient')

  return data
}

export async function getSuppliers(clientId): Promise<Supplier[]> {
  const { data } = await axios.get('/api/Boats/GetSuppliersForClient', { params: { clientId } })

  return data
}
export async function getSuppliersForBoat(boatId) {
  const { data } = await axios.get(`api/Boats/GetSuppliersForBoat/?boatId=${boatId}`)

  return data
}

export async function getProductCategories() {
  const { data } = await axios.get('/api/product-categories')

  return data
}

export async function getClientProductCategories(clientId: number) {
  const { data } = await axios.get(`/api/client/${clientId}/product-categories`)
  return data
}

export async function getProductCategory(key, id) {
  const { data } = await axios.get(`/api/product-categories/${id}`)

  return data
}

export async function createProductCategory(form) {
  const { data } = await axios.post('/api/product-categories', form)

  return data
}

export async function updateProductCategory({ id, form }) {
  const { data } = await axios.put(`/api/product-categories/${id}`, form)

  return data
}

export async function deleteProductCategory(id) {
  const { data } = await axios.delete(`/api/product-categories/${id}`)

  return data
}

export async function getProductUnits() {
  const { data } = await axios.get('/api/product-units')

  return data
}

export async function getProductUnit(key, id) {
  const { data } = await axios.get(`/api/product-units/${id}`)

  return data
}

export async function createProductUnit(form) {
  const { data } = await axios.post('/api/product-units', form)

  return data
}

export async function updateProductUnit({ id, form }) {
  const { data } = await axios.put(`/api/product-units/${id}`, form)

  return data
}

export async function deleteProductUnit(id) {
  const { data } = await axios.delete(`/api/product-units/${id}`)

  return data
}

export async function getProductRequests() {
  const { data } = await axios.get('/api/product-requests')

  return data
}

export async function approveProductRequest(id) {
  const { data } = await axios.post(`/api/product-requests/${id}/approve`)

  return data
}

export async function rejectProductRequest(id) {
  const { data } = await axios.post(`/api/product-requests/${id}/reject`)

  return data
}

export async function getTransactionRequests() {
  const { data } = await axios.get('/api/transaction-requests')

  return data
}

export async function approveTransactionRequest(id) {
  const { data } = await axios.post(`/api/transaction-requests/${id}/approve`)

  return data
}

export async function rejectTransactionRequest(id) {
  const { data } = await axios.post(`/api/transaction-requests/${id}/reject`)

  return data
}

export async function getProductGroups(key, clientId, exclude) {
  const params: any = {}

  if (exclude) {
    params.exclude = exclude
  }
  const { data } = await axios.get('/api/product-groups', {
    params: { params },
    paramsSerializer: params => qs.stringify(params),
  })

  return data
}

export async function getProductGroupValidChildren(key, id) {
  const { data } = await axios.get(`/api/product-groups/${id}/valid-children`)

  return data
}

export async function getProductGroup(key, id) {
  const { data } = await axios.get(`/api/product-groups/${id}`)

  return data
}

export async function getProductGroupChildren(key, id) {
  const { data } = await axios.get(`api/product-groups/${id}/children`)

  return data
}

export async function createProductGroup(form) {
  const { data } = await axios.post('/api/product-groups', form)

  return data
}

export async function updateProductGroup({ id, form }) {
  const { data } = await axios.put(`/api/product-groups/${id}`, form)

  return data
}

export async function deleteProductGroup(id) {
  const { data } = await axios.delete(`/api/product-groups/${id}`)

  return data
}

export async function getAbcCategories() {
  const { data } = await axios.get('/api/abc-categories')

  return data
}

export async function getAbcCategorization(key, stockId) {
  const { data } = await axios.get(`/api/stocks/${stockId}/abc-categorization`)

  return data
}

export async function performAbcCategorization(stockId) {
  const { data } = await axios.post(`/api/stocks/${stockId}/abc-categorization`)

  return data
}

export async function getOrderSuggestions(key, stockId) {
  const { data } = await axios.get(`/api/stocks/${stockId}/order-suggestions`)

  return data
}

export async function hideAllOrderSuggestions(stockId) {
  const { data } = await axios.post(`/api/stocks/${stockId}/order-suggestions/hide-all`)

  return data
}

export async function hideOrderSuggestion({ stockId, suggestionId }) {
  const { data } = await axios.post(`/api/stocks/${stockId}/order-suggestions/${suggestionId}/hide`)

  return data
}

export async function refreshOrderSuggestions(stockId) {
  const { data } = await axios.post(`/api/stocks/${stockId}/order-suggestions/refresh`)

  return data
}

export async function getBoat(key, boatId) {
  const { data, status } = await axios.get<IBoat>(`/api/Boats/${boatId}`)
  if (status == 401) {
    // we are either trying to open a boatpage that does not exist, or a boat that we dont have access to, redirect to boatlist
    history.push('/boatlist')
  }

  axios.defaults.headers['BoatPermission'] = data.encryptedPermissionValue
  return data
}

export async function getClientBoats(_, sortOn) {
  const { data } = await axios.get<IBoatListResult[]>(`/api/boats?sortBy=${sortOn}`)

  return data
}

export async function getDeadlinesForClient(clientId = null) {
  const { data } = await axios.get<ClientAndBoatDeadlines[]>(`/api/client/GetDeadlinesForClientAndBoats/${clientId}`)

  return data
}
export async function getAccessGroupsForClient(_key) {
  // clientId is found backend
  const { data } = await axios.get('/api/client/GetAccessGroupsForClient')

  return data
}

// gets users with access to a vessel with minimal other data, and a name prop instead of fullName
export async function getBoatUsers(key, boatId, token): Promise<BoatUser[]> {
  const { data } = await axios.get(`api/boats/GetUsersForBoat?boatId=${boatId}`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getAisPositionForBoats(key) {
  const { data } = await axios.get('/api/boats/GetAllAisPositions')

  return data
}

export async function getAisPositionForBoat(boatId: EntityId) {
  const { data } = await axios.get('/api/boats/GetAisPosition', { params: { boatId } })

  return data
}

export async function getUserClients() {
  const { data } = await axios.get('/api/Client/GetClientsForUser')

  return data
}
export async function getUserClientsForBoatlist() {
  const { data } = await axios.get('/api/Client/GetClientsForUserBoatList')

  return data
}
export async function getAdminUserClients(adminLinks = true) {
  const { data } = await axios.get(`/api/Client/GetClientsForUser?adminLinks=${adminLinks}`)
  return data
}
export async function getAdminUserClientsWithPartners(adminLinks = true): Promise<IClientMinimal[]> {
  const { data } = await axios.get(`/api/Client/GetClientsWithPartnersForUser?adminLinks=${adminLinks}`)
  return data
}

export async function getWorkOrders(key, boatId) {
  const { data } = await axios.get(`/api/boats/${boatId}/work-orders`)

  return data
}

export async function getWorkOrder(key, boatId, id) {
  const { data } = await axios.get(`/api/boats/${boatId}/work-orders/${id}`)

  return data
}

export async function createWorkOrder({ boatId, form }) {
  const { data } = await axios.post(`api/boats/${boatId}/work-orders`, form)

  return data
}

type UWOProps = {
  boatId: number
  id: number
  form: any
  token?: string
}

export async function updateWorkOrder({ boatId, id, form, token }: UWOProps) {
  const { data } = await axios.put(`api/boats/${boatId}/work-orders/${id}`, form, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

interface IUpdateWorkOrderItem {
  id: number
  items: any[]
}

export async function updateWorkOrderItems(updateWorkOrderItem: IUpdateWorkOrderItem) {
  const { data } = await axios.put(`api/workshops/work-orders/${updateWorkOrderItem.id}`, updateWorkOrderItem.items)
  return data
}
export async function updateWorkShopWorkOrder({ id, form }) {
  const { data } = await axios.put(`api/workshops/${id}/work-orders/`, form)

  return data
}

type TWOSProps = {
  boatId: number
  workOrderId: number
  token?: string
}

export async function toggleWorkOrderStatus({ boatId, workOrderId, token }: TWOSProps): Promise<any> {
  const { data } = await axios.put(
    `api/boats/${boatId}/work-orders/${workOrderId}/toggle-status`,
    {},
    { headers: getShareTokenAuthHeader(token) },
  )

  return data
}
export async function changeWorkOrderCaseStatus({ workshopId, workorderCase }) {
  const { data } = await axios.post(`api/workshops/${workshopId}/work-order/change-status`, workorderCase)

  return data
}

export async function UpdateWorkshopBoat({ workshopId, boatId, workshopBoatCase }) {
  const { data } = await axios.put(`api/workshops/${workshopId}/workshop-boats`, workshopBoatCase)
  return data
}
export async function updateWorkOrderCase({ workshopId, workorderCase }) {
  const { data } = await axios.post(`api/workshops/${workshopId}/work-order/update-case`, workorderCase)

  return data
}

interface IAddNewWorkOrder {
  workshopId: number
  form: any[]
}

export async function addNewWorkOrder(newWorkOrder: IAddNewWorkOrder) {
  const { data } = await axios.post(`api/workshops/${newWorkOrder.workshopId}/work-orders/`, newWorkOrder.form)

  return data
}

export async function deleteWorkOrder({ boatId, id }) {
  const { data } = await axios.delete(`api/boats/${boatId}/work-orders/${id}`)

  return data
}

export async function getBoatDeviations(key, boatId) {
  const { data } = await axios.get(`api/boats/${boatId}/deviations`)

  return data
}

export async function getBoatDeviationsForWorkorders(key, boatId) {
  const { data } = await axios.get(`api/boats/${boatId}/deviationsForWorkorders`)

  return data
}

export async function getDeviationById(deviationId): Promise<Deviation> {
  const { data } = await axios.get(`api/deviation/${deviationId}`)

  return data
}

export async function getDeviation(key, deviationId, token) {
  const { data } = await axios.get('api/Deviation/ById', {
    params: { deviationId },
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getDeviationStatuses(key, token): Promise<DeviationStatus[]> {
  const { data } = await axios.get('api/deviation/GetStatuses', { headers: getShareTokenAuthHeader(token) })

  return data
}

export async function createDeviationFollowup({ token, form }) {
  const { data } = await axios.post('api/deviation/CreateDeviationFollowUp', form, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getBoatEquipment(key, boatId): Promise<Equipment[]> {
  const { data } = await axios.get(`api/boats/${boatId}/equipments`)

  return data
}

export async function getEquipment(key, { equipmentId, token = undefined }) {
  const { data } = await axios.get(`api/Equipment/${equipmentId}`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getEquipmentDetails(key, equipmentId) {
  const { data } = await axios.get('api/Equipment/GetDetails', { params: { equipmentId } })

  return data
}

export async function updateEquipment(equipment) {
  const { data } = await axios.patch('api/Equipment/update', equipment)

  return data
}

export async function getDocumentCategories(key, token): Promise<DocumentCategory[]> {
  const { data } = await axios.get('api/document/GetDocumentCategories', { headers: getShareTokenAuthHeader(token) })

  return data
}

export async function getDocumentsForUser(key) {
  const { data } = await axios.get('api/user/GetDocuments')

  return data
}
export async function getDocumentUserCategories(key) {
  const { data } = await axios.get('api/document/GetDocumentUserCategories')

  return data
}

export async function getEquipmentDocuments(key, equipmentId, token) {
  const { data } = await axios.get('api/document/GetDocumentsForEquipment', {
    params: { equipmentId },
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function createDocument({ documentFile, token, boatId }) {
  const formData = new FormData()
  for (const key in documentFile) {
    if (documentFile[key]) formData.append(key, documentFile[key])
  }
  const { data } = await axios.post(`api/boats/${boatId}/documents`, formData, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}
export async function createDocumentToTemplate({ documentFile, token }) {
  const formData = new FormData()
  for (const key in documentFile) {
    if (documentFile[key]) formData.append(key, documentFile[key])
  }
  const { data } = await axios.post('api/document', formData, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function saveDocumentForUser({ documentFile }) {
  const formData = new FormData()

  for (const key in documentFile) {
    if (documentFile[key]) formData.append(key, documentFile[key])
  }
  const { data } = await axios.post('api/user/SaveDocument', formData)
  return data
}

export async function deleteDocumentForUser(docId) {
  const formData = new FormData()
  formData.append('documentIdForDeleting', docId)
  const { data } = await axios.post('api/user/DeleteDocument', formData)
  return data
}

export async function getEquipmentHistory(equipmentId: number) {
  const { data } = await axios.get(`api/equipment/${equipmentId}/equipment-history`)
  return data
}

export async function deleteDocument({ documentId, token }) {
  const { data } = await axios.post('api/document/DeleteDocument', undefined, {
    params: { documentId },
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function bulkDeleteDocuments(documentIds: EntityId[]) {
  const { data } = await axios.delete('api/document/bulk-delete-documents', {
    params: { documentIds },
    paramsSerializer: qs.stringify,
  })

  return data
}

export async function getBoatServices(key, boatId) {
  const { data } = await axios.get('api/service/GetCounterServices', { params: { boatId } })

  return data
}

export async function workOrderServicePerform({ workOrderItemId, services }) {
  const { data } = await axios.post(`api/service/complete-service/${workOrderItemId}`, { services })
  return data
}

export async function getWorkOrderSections(key, boatId, workOrderId) {
  const { data } = await axios.get(`api/boats/${boatId}/work-orders/${workOrderId}/sections`)

  return data
}

export async function getWorkOrderSection(key, boatId, workOrderId, sectionId) {
  const { data } = await axios.get(`api/boats/${boatId}/work-orders/${workOrderId}/sections/${sectionId}`)

  return data
}

export async function createWorkOrderSection({ boatId, workOrderId, form }) {
  const { data } = await axios.post(`api/boats/${boatId}/work-orders/${workOrderId}/sections`, form)

  return data
}

export async function updateWorkOrderSection({ boatId, workOrderId, form }) {
  const { data } = await axios.put(`api/boats/${boatId}/work-orders/${workOrderId}/}`, form)

  return data
}
type WOSIData = {
  boatId: number
  workOrderId: number
  sectionId: number
  itemId: number
  form: any
  token?: string
}

export async function updateWorkOrderSectionItem({ boatId, workOrderId, sectionId, itemId, form, token }: WOSIData) {
  const { data } = await axios.put(
    `api/boats/${boatId}/work-orders/${workOrderId}/sections/${sectionId}/${itemId}`,
    form,
    {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'X-Share-Token': token,
        ...authHeader(),
      },
    },
  )

  return data
}

export async function updateWorkOrderSectionItemPublic({ token, itemId, form }) {
  const { data } = await axios.put(`api/public/work-order-section/update-item/${itemId}`, form, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function toggleWorkOrderSectionStatus({ boatId, workOrderId }) {
  const { data } = await axios.put(`api/boats/${boatId}/work-orders/${workOrderId}/toggle-status`)

  return data
}

export async function deleteWorkOrderSection({ boatId, workOrderId, sectionId }) {
  const { data } = await axios.delete(`api/boats/${boatId}/work-orders/${workOrderId}/sections/${sectionId}`)

  return data
}

export async function deleteWorkOrderSectionItem({ boatId, workOrderId, itemId }) {
  const { data } = await axios.delete(`api/boats/${boatId}/work-orders/${workOrderId}/sections/delete-item/${itemId}`)

  return data
}

export async function getWorkOrderShareLink(key, { boatId, workOrderId, validUntil }) {
  const { data } = await axios.get(`api/boats/${boatId}/work-orders/${workOrderId}/share-link`, {
    params: { validUntil },
  })

  return data
}

export async function getWorkOrderSectionShareLink(key, { boatId, workOrderId, sectionId }) {
  const { data } = await axios.get(`api/boats/${boatId}/work-orders/${workOrderId}/sections/${sectionId}/share-link`)

  return data
}

export async function getWorkOrderFromToken(key, token) {
  const { data } = await axios.get('api/public/work-order', { headers: getShareTokenAuthHeader(token) })

  return data
}
export async function getWorkOrderSectionFromToken(key, token, sectionId) {
  const url = sectionId ? `api/public/work-order-section/${sectionId}` : 'api/public/work-order-section'
  const { data } = await axios.get(url, { headers: getShareTokenAuthHeader(token) })

  return data
}

export async function updateWorkOrderSectionPublic({ token, form, sectionId }) {
  const url = sectionId ? `api/public/work-order-section/update/${sectionId}` : 'api/public/work-order-section/update'
  const { data } = await axios.put(url, form, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function toggleWorkOrderStatusPublic(token) {
  const { data } = await axios.put('api/public/work-order/toggle-status', null, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function toggleWorkOrderSectionStatusPublic({ token, sectionId }) {
  const url = sectionId
    ? `api/public/work-order-section/toggle-status/${sectionId}`
    : 'api/public/work-order-section/toggle-status'

  const { data } = await axios.put(url, null, { headers: getShareTokenAuthHeader(token) })

  return data
}

export async function getSectionTemplates() {
  const { data } = await axios.get('api/section-templates')

  return data
}

export async function deleteSectionTemplate(id) {
  const { data } = await axios.delete(`api/section-templates/${id}`)

  return data
}

export async function createSectionTemplate(form) {
  const { data } = await axios.post('api/section-templates', form)

  return data
}

export async function getSectionFromTemplate(templateId) {
  const { data } = await axios.post(`api/section-templates/${templateId}/generate-section`)

  return data
}

export async function getDataSources() {
  const { data } = await axios.get('api/data-sources')

  return data
}

export async function getDataSourcesByType(key, type) {
  const { data } = await axios.get(`api/data-sources/${type}`)

  return data
}

export async function getDataSource(key, id) {
  const { data } = await axios.get(`api/data-sources/${id}`)

  return data
}

export async function getDataFromDataSource(key, dataSourceId, token?) {
  const { data } = await axios.get(`api/data-sources/${dataSourceId}/get-data`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function createDataSource(form) {
  const { data } = await axios.post('api/data-sources', form)

  return data
}

export async function updateDataSource({ id, form }) {
  const { data } = await axios.put(`api/data-sources/${id}`, form)

  return data
}

export async function deleteDataSource(id) {
  const { data } = await axios.delete(`api/data-sources/${id}`)

  return data
}

export async function deleteDataSourceItem({ dataSourceId, id }) {
  const { data } = await axios.delete(`api/data-sources/${dataSourceId}/items/${id}`)

  return data
}

export async function getChecklists(_, { type = undefined, client = undefined } = {}) {
  const { data } = await axios.get<IChecklist[]>('api/checklists', {
    params: { type, client },
  })

  return data
}

export async function getChecklistsByWorkshop(key, params) {
  const { data } = await axios.get<IChecklist[]>('api/checklists/byworkshop', {
    params: params,
  })

  return data
}

export async function getChecklistsByWorkshopBoat(key, params) {
  const { data } = await axios.get<IChecklist[]>('api/checklists/workshop-boat', {
    params: params,
  })

  return data
}

export async function getChecklist(key, id): Promise<Checklist> {
  const { data } = await axios.get(`api/checklists/${id}`)

  return data
}

export async function getChecklistItems(_, checklistId, type) {
  const { data } = await axios.get(`api/checklists/${checklistId}/items/${type}`)

  return data
}

export async function getStartupRoutineItems(_, checklistId, boatRoutineId): Promise<ChecklistItem[]> {
  const { data } = await axios.get(`api/checklists/${checklistId}/startup-routine-items/${boatRoutineId}`)

  return data
}

export async function createCheckList() {
  const { data } = await axios.post('api/checklists', {})

  return data
}

export async function createCheckListWithItems(form) {
  const { data } = await axios.post('api/checklists/create', form)

  return data
}

export async function updateCheckList({ id, form }) {
  const { data } = await axios.put(`api/checklists/${id}`, form)
  return data
}

export async function deleteChecklist(id) {
  const { data } = await axios.delete(`api/checklists/${id}`)

  return data
}

export async function deleteChecklistItem({ checklistId, id }) {
  const { data } = await axios.delete(`api/checklists/${checklistId}/items/${id}`)

  return data
}

export async function getChecklistCompletions(key, filters) {
  const { data } = await axios.get('api/checklist-completions', { params: filters })

  return data
}

export async function getChecklistCompletion(key, id, token = undefined) {
  const { data } = await axios.get(`api/checklist-completions/${id}`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getWorkshpopCheckList(key, id, token = undefined) {
  const { data } = await axios.get(`api/checklist-completions/${id}`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function toggleCompleteChecklistCompletion(id) {
  const { data } = await axios.post(`api/checklist-completions/${id}/toggle-complete`)

  return data
}

export async function updateChecklistCompletionItem({ id, itemId, form, token = undefined }) {
  const { data } = await axios.put(`api/checklist-completions/${id}/update-item/${itemId}`, form, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function createChecklistCompletion(form) {
  const { data } = await axios.post('api/checklist-completions', form)

  return data
}

export async function deleteChecklistCompletion(id) {
  const { data } = await axios.delete(`api/checklist-completions/${id}`)

  return data
}

export async function generateChecklistCompletionPdf(completionId) {
  const { data } = await axios.post(`api/checklist-completions/generate-pdf/${completionId}`)

  return data
}

export async function getWorkOrderTemplates() {
  const { data } = await axios.get('api/work-order-templates')

  return data
}

export async function getWorkOrderTemplate(_, id) {
  const { data } = await axios.get(`api/work-order-templates/${id}`)

  return data
}

export async function createWorkOrderTemplate(form) {
  const { data } = await axios.post('api/work-order-templates', form)

  return data
}

export async function updateWorkOrderTemplate({ id, form }) {
  const { data } = await axios.put(`api/work-order-templates/${id}`, form)

  return data
}

export async function createWorkOrderTemplateSection({ workOrderId, form }) {
  const { data } = await axios.post(`api/work-order-templates/${workOrderId}/sections`, form)

  return data
}

export async function updateWorkOrderTemplateSection({ workOrderId, sectionId, form }) {
  const { data } = await axios.put(`api/work-order-templates/${workOrderId}/sections/${sectionId}`, form)

  return data
}

export async function deleteWorkOrderTemplate(workOrderId) {
  const { data } = await axios.delete(`api/work-order-templates/${workOrderId}`)

  return data
}

export async function deleteWorkOrderTemplateSection({ workOrderId, sectionId }) {
  const { data } = await axios.delete(`api/work-order-templates/${workOrderId}/sections/${sectionId}`)

  return data
}

export async function deleteWorkOrderTemplateSectionItem({ workOrderId, itemId }) {
  const { data } = await axios.delete(`api/work-order-templates/${workOrderId}/sections/delete-item/${itemId}`)

  return data
}

export async function generateWorkOrderFromTemplate({ templateId, form }) {
  const { data } = await axios.post(`api/work-order-templates/${templateId}/generate-work-order`, form)

  return data
}

export async function getBoatCountersByType(boatId: EntityId, counterType: BoatCounterType) {
  switch (counterType) {
    case BoatCounterType.Hour:
      return getBoatHourCounters(boatId)
    case BoatCounterType.ManualConsumption:
      return getBoatFuelCounters(boatId)
  }
}

export async function getBoatCounters(boatId: EntityId) {
  const { data } = await axios.get(`api/service/GetCountersForBoat/${boatId}`)

  return data
}

export async function getBoatHourCounters(boatId: EntityId) {
  const { data } = await axios.get(`api/service/hour-counters-for-boat/${boatId}`)

  return data
}

export async function getBoatCounterInfo(boatCounterId: EntityId): Promise<BoatCounterInfo> {
  const { data } = await axios.get(`api/service/boatCounterInfo/${boatCounterId}`)

  return data
}
export async function getBoatFuelCounters(boatId: EntityId) {
  const { data } = await axios.get(`api/service/fuel-counters-for-boat/${boatId}`)

  return data
}

export async function getConnectedServices(key, boatCounterLogId: EntityId): Promise<BoatCounterLogServicesMinimum[]> {
  const { data } = await axios.get(`api/service/ConnectedServicelogs/${boatCounterLogId}`)

  return data
}

export async function getStartupRoutines() {
  const { data } = await axios.get('api/startup-routines')

  return data
}

export async function getStartupRoutine(_, id) {
  const { data } = await axios.get(`api/startup-routines/${id}`)

  return data
}

export async function getStartupRoutineForDashboard(_, boatId, token?): Promise<BoatRoutineContainer> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/dashboardRoutine`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getStartupRoutineForBoat(_, boatId, token = undefined): Promise<BoatStartupRoutine> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/dashboard`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getShutdownRoutineForBoat(_, boatId, token?): Promise<BoatShutdownRoutine> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/shutdownRoutine`, {
    headers: getShareTokenAuthHeader(token),
  })
  return data
}
export async function getCheckinsForUser(_, boatId, boatRoutine, token?): Promise<CrewCheckin[]> {
  const { data } = await axios.get(
    `api/boats/${boatId}/startup-routines/shutdownRoutine/${boatRoutine}/check-ins-user`,
    {
      headers: getShareTokenAuthHeader(token),
    },
  )
  return data
}

export async function getCrewCheckinsForShutdown(_, boatId, boatRoutineId, token?): Promise<CrewCheckin[]> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/shutdownRoutine/${boatRoutineId}/checkins`, {
    headers: getShareTokenAuthHeader(token),
  })
  return data
}

export async function createStartupRoutine(form: CreateStartupRoutineReq) {
  const { data } = await axios.post('api/startup-routines', form)

  return data
}

export async function updateStartupRoutine({ id, form }: { id: number; form: CreateStartupRoutineReq }) {
  const { data } = await axios.put(`api/startup-routines/${id}`, form)

  return data
}

export async function deleteStartupRoutine(id) {
  const { data } = await axios.delete(`api/startup-routines/${id}`)

  return data
}

export async function getValidBoatsForStartupRoutines(_, routineId) {
  const { data } = await axios.get(`api/startup-routines/${routineId}/valid-boats`)

  return data
}

export async function showBoatsWithExercise(exerciseId) {
  const { data } = await axios.get(`api/exercises/used-on-boat/${exerciseId}`)

  return data
}

export async function toggleRoutineBoatConnection({ routineId, boatId }) {
  const { data } = await axios.post(`api/startup-routines/${routineId}/toggle-boat-connection/${boatId}`)

  return data
}

export async function createCounterChecklistConnection({ routineId, ...rest }) {
  const { data } = await axios.post(`api/startup-routines/${routineId}/create-counter-connection`, rest)

  return data
}

export async function getChecklistItemCounter(boatId, itemId, token = undefined) {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/item-counters/${itemId}`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getCrewCheckinsForClient(_): Promise<CrewCheckin[]> {
  const { data } = await axios.get('api/startup-routines/getCheckinsForClient')
  return data
}
export async function findMissingCheckouts(_key, clientId): Promise<CrewCheckin[]> {
  const { data } = await axios.get(`api/startup-routines/findMissingCheckouts/${clientId}`)

  return data
}
export async function fixMissingCheckouts(clientId, checkArray): Promise<any> {
  const { data } = await axios.post(`api/startup-routines/fixMissingCheckouts/${clientId}`, checkArray)

  return data
}

export async function findDuplicateServiceOccurences(key, clientId): Promise<any> {
  const { data } = await axios.get(`api/service/FindDuplicateServiceOccurrences/${clientId || 0}`)

  return data
}

export async function removeDuplicateServiceOccurences(clientId): Promise<any> {
  const { data } = await axios.post(`api/service/RemoveDuplicateServiceOccurrences/${clientId || 0}`)

  return data
}
export async function getBoatRoutineCheckIns(_, boatId, boatRoutineId, token = undefined): Promise<DailyCheckin[]> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/${boatRoutineId}/check-ins`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getBoatRoutineCheckInsForDaily(
  _,
  boatId,
  boatRoutineId,
  dailyId,
  token = undefined,
): Promise<DailyCheckin[]> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/${boatRoutineId}/${dailyId}/check-ins`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getBoatRoutineDailyCheckIns(_, boatId, dailyId, token = undefined): Promise<CrewCheckin[]> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/dailies/${dailyId}/check-ins`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function createCrewCheckinEntry({ boatId, boatRoutineId, token = undefined, ...rest }) {
  const { data } = await axios.post(`api/boats/${boatId}/startup-routines/${boatRoutineId}/check-ins`, rest, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}
export async function createCrewCheckinEntryShutdown({ boatId, boatShutdownRoutineId, token = undefined, ...rest }) {
  const { data } = await axios.post(
    `api/boats/${boatId}/startup-routines/${boatShutdownRoutineId}/check-ins-shutdown`,
    rest,
    {
      headers: getShareTokenAuthHeader(token),
    },
  )

  return data
}

export async function deleteCrewCheckinEntry({ boatId, boatRoutineId, checkinId, token = undefined }) {
  const { data } = await axios.delete(`api/boats/${boatId}/startup-routines/${boatRoutineId}/check-ins/${checkinId}`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getPopularCheckins(_, boatId, boatRoutineId, token = undefined) {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/${boatRoutineId}/check-ins/popular`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function completeBoatRoutine({ boatId, boatRoutineId, token = undefined }) {
  const { data } = await axios.post(
    `api/boats/${boatId}/startup-routines/${boatRoutineId}/complete`,
    {},
    {
      headers: getShareTokenAuthHeader(token),
    },
  )

  return data
}
export async function completeShutdownRoutine({ boatId, boatShutdownRoutineId, token = undefined }) {
  const { data } = await axios.post(
    `api/boats/${boatId}/startup-routines/${boatShutdownRoutineId}/completeShutdown`,
    {},
    {
      headers: getShareTokenAuthHeader(token),
    },
  )

  return data
}
export async function saveCheckin({
  boatId,
  crewCheckin,
  token,
}: {
  boatId: number
  crewCheckin: CrewCheckin
  token?: string
}) {
  const { data } = await axios.post(`api/boats/${boatId}/startup-routines/crewCheckinUpdate`, crewCheckin, {
    headers: getShareTokenAuthHeader(token),
  })
  return data
}

export async function saveCheckinBulk({
  boatId,
  crewCheckin,
  token,
}: {
  boatId: number
  crewCheckin: CrewCheckin[]
  token?: string
}) {
  const { data } = await axios.post(`api/boats/${boatId}/startup-routines/crewCheckinBulkUpdate`, crewCheckin, {
    headers: getShareTokenAuthHeader(token),
  })
  return data
}

export async function getDefaultRoleForUser(_, boatId, token = undefined): Promise<number> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/defaultRoleForUser`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getBoatRoutineChecklistCompletion(
  _,
  boatId,
  boatRoutineId,
  token = undefined,
): Promise<CheckListCompletion> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/${boatRoutineId}/checklist-completion`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getBoatRoutineChecklistCompletionForDaily(
  _,
  boatId,
  boatRoutineId,
  dailyId,
  token = undefined,
): Promise<CheckListCompletion> {
  const { data } = await axios.get(
    `api/boats/${boatId}/startup-routines/${boatRoutineId}/${dailyId}/checklist-completion`,
    {
      headers: getShareTokenAuthHeader(token),
    },
  )

  return data
}

export async function getBoatShutdownRoutineChecklistCompletion(
  _,
  boatId: number,
  boatRoutineId: number,
  token: string = undefined,
): Promise<CheckListCompletion> {
  const { data } = await axios.get(
    `api/boats/${boatId}/startup-routines/${boatRoutineId}/checklist-completion-shutdown`,
    {
      headers: getShareTokenAuthHeader(token),
    },
  )

  return data
}

export async function getBoatRoutineDailyChecklistCompletion(_, boatId, dailyId, token = undefined) {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/dailies/${dailyId}/checklist-completion`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function createBoatCounterEntry({
  boatCounterId,
  boatCounterLogTypeId = 2,
  value,
  notes,
  token = undefined,
}) {
  const { data } = await axios.post(
    'api/service/CreateBoatCounterLog',
    {
      boatCounterId,
      boatCounterLogTypeId,
      value,
      notes,
    },
    {
      headers: getShareTokenAuthHeader(token),
    },
  )

  return data
}

export async function getBoatRoutineHistorySingle(_, boatId, routineId): Promise<StartupRoutineDaily> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/${routineId}/historySingle`)
  return data
}

export async function getBoatRoutineHistoryMinimal(_, boatId): Promise<StartupRoutineHistoryMinimal[]> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/historyMinimal`)

  return data
}

export async function getBoatRoutineHistory(_, boatId): Promise<StartupRoutineDaily[]> {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/history`)

  return data
}

export async function getBoatChecklists(_, boatId, equipmentId) {
  const url = equipmentId
    ? `api/boats/${boatId}/checklists?equipmentId=${equipmentId}`
    : `api/boats/${boatId}/checklists`
  const { data } = await axios.get(url)

  return data
}

export async function createBoatChecklist({ boatId, form }) {
  const { data } = await axios.post(`api/boats/${boatId}/checklists`, form)

  return data
}

export async function getRiskAnalysisTasks() {
  const { data } = await axios.get<IRiskAnalysisTask[]>('/api/risk-analysis/tasks')

  return data
}

export async function getRiskAnalysisTask(id: EntityId) {
  const { data } = await axios.get<IRiskAnalysisTask>(`/api/risk-analysis/tasks/${id}`)

  return data
}

export async function getRiskAnalysisSubTasks(taskId: EntityId) {
  const { data } = await axios.get<IRiskAnalysisSubTask[]>(`/api/risk-analysis/tasks/${taskId}/subtasks`)

  return data
}

export async function createRiskAnalysisTask(form) {
  const { data } = await axios.post('/api/risk-analysis/tasks', form)

  return data
}

export async function updateRiskAnalysisTask({ taskId, form }) {
  const { data } = await axios.post(`/api/risk-analysis/tasks/${taskId}`, form)

  return data
}

export async function deleteRiskAnalysisTask(id: string | number) {
  const { data } = await axios.delete(`/api/risk-analysis/tasks/${id}`)

  return data
}

export async function deleteRiskAnalysisSubTask({ taskId, subTaskId }) {
  const { data } = await axios.delete(`/api/risk-analysis/tasks/${taskId}/subtasks/${subTaskId}`)

  return data
}

export async function getBoatRiskAnalysisInfo(boatId: number) {
  const { data } = await axios.get<IBoatRiskAnalysisGeneralInfo>(`/api/boats/${boatId}/risk-analysis/general-info`)

  return data
}

export async function getBoatRiskAnalysisTasks(boatId: number, riskLevel: RiskAnalysisRiskStatus, afterMeasures) {
  const { data } = await axios.get<IBoatRiskAnalysisTask[]>(`/api/boats/${boatId}/risk-analysis`, {
    params: { riskLevel, afterMeasures },
  })

  return data
}

export async function getAllBoatRiskAnalysisTasks(boatId: number) {
  const { data } = await axios.get<IBoatRiskAnalysisTask[]>(`/api/boats/${boatId}/risk-analysis/all`)

  return data
}
export async function getBoatRiskAnalysisSubTasks(boatId: number, taskId: number) {
  const { data } = await axios.get<IBoatRiskAnalysisSubTask[]>(`/api/boats/${boatId}/risk-analysis/${taskId}/subtasks`)

  return data
}

export async function getBoatRiskAnalysisSubTaskDangers(boatId: number, taskId: number, subTaskId: number) {
  const { data } = await axios.get<ISubTaskDanger[]>(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers`,
  )

  return data
}

export async function getBoatRiskAnalysisSubTaskDangerMeasures(
  boatId: number,
  taskId: number,
  subTaskId: number,
  dangerId: number,
) {
  const { data } = await axios.get<ISubTaskMeasure[]>(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers/${dangerId}/measures`,
  )

  return data
}

export async function getBoatRiskAnalysisSubTaskDangerMeasure(
  boatId: number,
  taskId: number,
  subTaskId: number,
  dangerId: number,
  measureId: number,
) {
  const { data } = await axios.get<ISubTaskMeasure[]>(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers/${dangerId}/measures/${measureId}`,
  )

  return data
}

export async function createBoatRiskAnalaysisTasks({ form, boatId }: { form: ICreateBoatTaskForm[]; boatId: number }) {
  const { data } = await axios.post(`/api/boats/${boatId}/risk-analysis`, form)

  return data
}

export async function createBoatRiskAnalysisSubTask({ boatId, taskId, name }) {
  const { data } = await axios.post(`/api/boats/${boatId}/risk-analysis/${taskId}/subtasks`, { name })

  return data
}

export async function createBoatSubTaskDanger({ boatId, taskId, subTaskId, name }) {
  const { data } = await axios.post(`/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers`, {
    name,
  })

  return data
}

export async function createBoatSubTaskMeasure({ boatId, taskId, subTaskId, dangerId, form }) {
  const { data } = await axios.post(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers/${dangerId}/measures`,
    form,
  )

  return data
}

export async function deleteFamiliarizationForm(familiarizationFormId: number) {
  const { data } = await axios.delete(`/api/clientadmin/${familiarizationFormId}`)

  return data
}

export async function updateBoatSubTaskMeasure({ boatId, taskId, subTaskId, dangerId, measureId, form }) {
  const { data } = await axios.put(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers/${dangerId}/measures/${measureId}`,
    form,
  )

  return data
}

export async function updateBoatSubTaskDanger({ boatId, taskId, subTaskId, dangerId, form }) {
  const { data } = await axios.put(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers/${dangerId}`,
    form,
  )

  return data
}

export async function updateBoatSubTaskDangerName({ boatId, taskId, subTaskId, dangerId, name }) {
  const { data } = await axios.post(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers/${dangerId}`,
    {
      name,
    },
  )

  return data
}

export async function deleteBoatTask({ boatId, taskId }) {
  const { data } = await axios.delete(`/api/boats/${boatId}/risk-analysis/${taskId}`)

  return data
}

export async function deleteBoatSubTask({ boatId, taskId, subTaskId }) {
  const { data } = await axios.delete(`/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}`)

  return data
}

export async function updateBoatSubTaskName({ boatId, taskId, subTaskId, name }) {
  const { data } = await axios.post(`/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}`, { name })

  return data
}

export async function deleteBoatSubTaskDanger({ boatId, taskId, subTaskId, dangerId }) {
  const { data } = await axios.delete(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers/${dangerId}`,
  )

  return data
}

export async function deleteBoatSubTaskMeasure({ boatId, taskId, subTaskId, dangerId, measureId }) {
  const { data } = await axios.delete(
    `/api/boats/${boatId}/risk-analysis/${taskId}/subtasks/${subTaskId}/dangers/${dangerId}/measures/${measureId}`,
  )

  return data
}

export async function deleteBoatRiskAnalysisEquipment({ boatId, equipmentId }) {
  const { data } = await axios.delete(`/api/boats/${boatId}/risk-analysis/delete-equipment/${equipmentId}`)

  return data
}

export async function uploadBoatRiskAnalysisMeasureAttachment({ boatId, formData }) {
  const { data } = await axios.post(`/api/boats/${boatId}/risk-analysis/upload-attachment`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...authHeader(),
    },
  })

  return data
}

type uploadData = {
  formData: FormData
  token?: string
  folderId?: number
}

export async function uploadFile({ token = null, formData, folderId }: uploadData) {
  const url = folderId ? `/api/file/v2/upload-file/${folderId}` : '/api/file/v2/upload-file'
  const { data } = await axios.post<IFile>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Access-Control-Allow-Origin': '*',
      'X-Share-Token': token,
      ...authHeader(),
    },
  })

  return data
}

export async function uploadImage({ token = null, formData }: uploadData) {
  const { data } = await axios.post<IFile>('/api/file/v2/upload-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Access-Control-Allow-Origin': '*',
      'X-Share-Token': token,
      ...authHeader(),
    },
  })

  return data
}

export async function getHasActiveCaptain(boatId, dailyId, token) {
  const { data } = await axios.get(`api/boats/${boatId}/startup-routines/hasActiveCaptain?dailyId=${dailyId}`, {
    headers: getShareTokenAuthHeader(token),
  })
  return data
}

export async function deleteFile(fileId: number) {
  const { data } = await axios.delete(`api/file/v2/delete-file/${fileId}`)

  return data
}

export async function deleteBoatRiskAnalysisMeasureAttachment({ boatId, attachmentId }) {
  const { data } = await axios.delete(`/api/boats/${boatId}/risk-analysis/delete-attachment/${attachmentId}`)

  return data
}

export async function getRiskAnalysisTasksSuperAdmin() {
  const { data } = await axios.get<IRiskAnalysisTask[]>('/api/intranet/risk-analysis/tasks')

  return data
}

export async function getSfiMainCategory({ level, parentId }): Promise<MainCategory[]> {
  const { data } = await axios.get<MainCategory[]>(`/api/Intranet/sfi/getMainCategories/${level}/${parentId}`)

  return data
}

export async function createNewCategory(category) {
  const { data } = await axios.post('/api/Intranet/sfi/createNewCategory', category)

  return data
}

export async function validateCode({ code }): Promise<boolean> {
  const { data } = await axios.get<boolean>(`/api/Intranet/sfi/validateCode/${code}`)

  return data
}

export async function getRiskAnalysisTaskSuperAdmin(id: EntityId) {
  const { data } = await axios.get<IRiskAnalysisTask>(`/api/intranet/risk-analysis/tasks/${id}`)

  return data
}

export async function createRiskAnalysisTaskSuperAdmin(form) {
  const { data } = await axios.post('/api/intranet/risk-analysis/tasks', form)

  return data
}

export async function updateRiskAnalysisTaskSuperAdmin({ taskId, form }) {
  const { data } = await axios.post(`/api/intranet/risk-analysis/tasks/${taskId}`, form)

  return data
}

export async function deleteRiskAnalysisTaskSuperAdmin(id: string | number) {
  const { data } = await axios.delete(`/api/intranet/risk-analysis/tasks/${id}`)

  return data
}

export async function deleteRiskAnalysisSubTaskSuperAdmin({ taskId, subTaskId }) {
  const { data } = await axios.delete(`/api/intranet/risk-analysis/tasks/${taskId}/subtasks/${subTaskId}`)

  return data
}

export async function getWorkshops() {
  const { data } = await axios.get<IWorkshop[]>('api/workshops')

  return data
}

export async function getWorkshop(id: EntityId) {
  const { data } = await axios.get<IGetWorkshopReponse>(`api/workshops/${id}`)
  return data.result
}

export async function createWorkshop(form) {
  const { data } = await axios.post<IWorkshop>('api/workshops', form)

  return data
}

export async function updateWorkshop({ id, form }) {
  const { data } = await axios.put<IWorkshop>(`api/workshops/${id}`, form)

  return data
}

export async function deleteWorkshop(id) {
  const { data } = await axios.delete(`api/workshops/${id}`)

  return data
}

export async function getWorkshopAvailableUsers(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopUser[]>(`api/workshops/${workshopId}/users/available`)
  return data
}
export async function getAllWorkshopUsers(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopUser[]>(`api/workshops/${workshopId}/users`)
  return data
}
export async function getWorkshopRoles() {
  const { data } = await axios.get<Role[]>('api/role/workshop')
  return data
}

export async function getWorkOrdersForWorkshopUser(workshopId: EntityId, userId: EntityId) {
  const { data } = await axios.get(`api/workshops/${workshopId}/users/${userId}/work-orders`)

  return data
}
export async function getBoatsForWorkshopUser(workshopId: EntityId, userId: EntityId) {
  const { data } = await axios.get(`api/workshops/${workshopId}/users/${userId}/boats`)

  return data
}
export async function getWorkOrdersByBoat(workshopId: EntityId, boatId: EntityId) {
  const { data } = await axios.get(`api/workshops/${workshopId}/boats/${boatId}/workorders`)
  return data
}
export async function getWorkshopBoatsForWorkshopUserCalendar(
  workshopId: EntityId,
  userId: EntityId,
  year: number,
  month: number,
) {
  const { data } = await axios.get(`api/workshops/${workshopId}/users/${userId}/boats/calendar/${year}/${month}`)

  return data
}

export async function getWorkshopBoatsForWorkshopCalendar(workshopId: EntityId, year: number, month: number) {
  const { data } = await axios.get(`api/workshops/${workshopId}/boats/calendar/${year}/${month}`)

  return data
}

// flesh out this type before full release
type WorkOrder = {
  [key: string]: any
}

export async function getWorkOrdersForWorkshopForSend(workshopId: EntityId, boatId: number): Promise<WorkOrder[]> {
  const { data } = await axios.get(`api/workshops/${workshopId}/${boatId}/send-workshop/work-orders`)
  return data
}

export async function addUserToWorkshop({ workshopId, form }) {
  const { data } = await axios.post(`api/workshops/${workshopId}/users`, form)

  return data
}
export async function addUserFromEmailToWorkshop({ id, form }) {
  const { data } = await axios.post(`api/workshops/${id}/users/email`, form)
  return data
}

export async function updateWorkshopUserRole({ id, form }) {
  const { data } = await axios.put(`api/workshops/${id}/users/update-role`, form)
  return data
}
export async function deleteWorkShopUser({ workshopId, userId }) {
  const { data } = await axios.delete(`api/workshops/${workshopId}/users/${userId}`)

  return data
}

export async function removeUserFromWorkshop({ workshopId, userId }) {
  const { data } = await axios.delete(`api/workshops/${workshopId}/users/${userId}`)

  return data
}

export async function getWorkshopsForBoat(boatId: EntityId) {
  const { data } = await axios.get<IWorkshop[]>(`api/workshops/boats/${boatId}`)

  return data
}

export async function getWorkshopsByClientId(clientId: EntityId) {
  const { data } = await axios.get<IWorkshop[]>(`api/workshops/client/${clientId}`)

  return data
}
export async function getWorkshopNotificationPreferenceForUser(workshopId: EntityId) {
  const { data } = await axios.get<WorkshopUserNotificationPreference>(
    `api/workshops/${workshopId}/user/notification-preference`,
  )

  return data
}
export async function updateWorkshopUserNotificationPreference(
  workshopId: EntityId,
  form: WorkshopUserNotificationPreference,
) {
  const { data } = await axios.post(`api/workshops/${workshopId}/user/notification-preference`, form)

  return data
}
export async function transferWorkorderToWorkshop({ workshopId, workorderId }) {
  const { data } = await axios.put(`api/workshops/workorders/${workorderId}/transfer/${workshopId}`)

  return data
}
export async function transferAcceptedWorkorderToWorkshop({ workshopId, workorderId }) {
  const { data } = await axios.put(`api/workshops/workorders/${workorderId}/transferAccepted/${workshopId}`)

  return data
}
export async function getBoatWorkshopStatus(boatId: EntityId) {
  const { data } = await axios.get<IWorkshopBoatStatusResponse>(`api/workshops/boats/${boatId}/status`)

  return data
}

export async function getBoatActiveWorkshop(boatId: EntityId) {
  const { data } = await axios.get<IWorkshopBoat>(`api/workshops/boats/${boatId}/active-workshop`)

  return data
}

export async function sendBoatToWorkshop({
  boatId,
  workshopId,
  connectedServices,
  workOrders,
  workshopBoatCheckLists,
  checklistItems,
}) {
  const { data } = await axios.post(`api/workshops/${workshopId}/boats/${boatId}`, {
    connectedServices,
    workOrders,
    workshopBoatCheckLists,
    checklistItems,
  })

  return data
}
export async function performWorkshopService({ wbId, workshopId, service }) {
  const { data } = await axios.post(`api/workshops/${workshopId}/boats/${wbId}/perform-service`, service)

  return data
}

export async function getMyWorkshops() {
  const { data } = await axios.get<IWorkshopOverview[]>('api/workshops/me')

  return data
}

export async function getWorkshopBoats(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopBoatResponse[]>(`api/workshops/${workshopId}/boats`)

  return data
}

export async function getWorkshopBoat(workshopId: EntityId, workshopBoatId: EntityId) {
  const { data } = await axios.get<IWorkshopBoat>(`api/workshops/${workshopId}/boats/${workshopBoatId}`)
  return data
}

export async function getWorkshopBoatHistory(workshopId: EntityId, workshopBoatId: EntityId) {
  const { data } = await axios.get(`api/workshops/${workshopId}/boats/${workshopBoatId}/history`)
  return data
}

export async function getWorkshopBoatAdditionalResources(workshopId: EntityId, workshopBoatId: EntityId) {
  const { data } = await axios.get<IWorkshopBoat>(
    `api/workshops/${workshopId}/boats/${workshopBoatId}/additional-resources`,
  )
  return data
}

export async function getWorkshopBoatStatus(workshopId: EntityId, workshopBoatId: EntityId) {
  const { data } = await axios.get<WorkshopBoatStatus>(`api/workshops/${workshopId}/boats/${workshopBoatId}/status`)
  return data
}

export async function getWorkshopWorkOrders(workshopId: EntityId) {
  const { data } = await axios.get<IWorkOrderPreview[]>(`api/workshops/${workshopId}/work-orders`)
  return data
}

export async function getPaginatedWorkshopWorkOrders({ pageParam = null, queryKey }) {
  const [_key, id, searchKeyword] = queryKey

  const { data } = await axios.get<IWorkOrderPaginatedResponse>(`api/workshops/${id}/work-orders/v2`, {
    params: {
      cursor: pageParam,
      pageSize: 10,
      searchKeyword: searchKeyword,
      includeTotalCount: !pageParam && !searchKeyword,
    },
  })
  return data
}

export async function getWorkshopWorkOrder(workshopId: EntityId, workOrderId: EntityId) {
  const { data } = await axios.get<IWorkOrder>(`api/workshops/${workshopId}/work-orders/${workOrderId}`)

  return data
}

export async function getWorkshopOverview(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopOverview>(`api/workshops/${workshopId}/overview`)
  axios.defaults.headers['X-Client-Id'] = data.clientId
  return data
}

export function setAxiosHeaders(headerName: string, value) {
  axios.defaults.headers[headerName] = value
}
export function removeAxiosHeaders(headerName: string) {
  delete axios.defaults.headers[headerName]
}

export async function getWorkshopActiveBoats(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopBoatResponse[]>(`api/workshops/${workshopId}/active-boats`)
  return data
}

export async function getActiveBoatsForSystemUser(workshopId: EntityId, userId: EntityId, date?: Date) {
  let endpointUrl = `api/workshops/${workshopId}/system-user/active-boats/${userId}`

  if (date) {
    const selectedDate = encodeURIComponent(date.toISOString())
    endpointUrl += `/${selectedDate}`
  }
  const { data } = await axios.get<IWorkshopBoatResponse[]>(endpointUrl)
  return data
}

export async function getWorkshopMyBoats(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopBoatResponse[]>(`api/workshops/${workshopId}/my-boats`)
  return data
}

export async function getInProgressWorkshopMyBoats(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopBoatResponse[]>(`api/workshops/${workshopId}/inprogress-boats`)
  return data
}

export async function getPaginatedPlannedWorkshopWorkCases({ pageParam = null, queryKey }) {
  const [_key, workshopId, searchKeyword] = queryKey

  const { data } = await axios.get<IWorkCasesPaginatedResponse>(`api/workshops/${workshopId}/planned-workcases`, {
    params: {
      cursor: pageParam,
      pageSize: 10,
      searchKeyword: searchKeyword,
      includeTotalCount: !pageParam && !searchKeyword,
    },
  })
  return data
}

export async function getPendingApprovalWorkshopWorkCases(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopWorkOrderSectionResponse[]>(
    `api/workshops/${workshopId}/pending-approval-workcases`,
  )
  return data
}
export async function getOverdueWorkshopWorkCases(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopWorkOrderSectionResponse[]>(
    `api/workshops/${workshopId}/overdue-overview-workcases`,
  )
  return data
}

export async function getTotalWorkCases(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopWorkOrderSectionResponse[]>(
    `api/workshops/${workshopId}/total-overview-workcases`,
  )
  return data
}

export async function getMyBoatsWorkshopWorkCases(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopWorkOrderSectionResponse[]>(`api/workshops/${workshopId}/my-boats`)
  return data
}

export async function getCompletedBoatsWorkshopWorkCases(
  workshopId: EntityId,
  search: string,
  page: number,
  pageSize: number,
) {
  const { data } = await axios.get<IWorkshopWorkOrderSectionResponse[]>(`api/workshops/${workshopId}/completed-boats`, {
    params: { search, page, pageSize },
  })
  return data
}

export async function getCompletedPaginatedWorkcases({ pageParam = null, queryKey }) {
  const [_key, id, searchKeyword] = queryKey

  const { data } = await axios.get<IWorkCasesPaginatedResponse>(`api/workshops/${id}/workcases/completed`, {
    params: {
      cursor: pageParam,
      pageSize: 10,
      searchKeyword: searchKeyword,
      includeTotalCount: !pageParam && !searchKeyword,
    },
  })
  return data
}

export async function getActiveWorkshopBoats(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopBoatResponse[]>(`api/workshops/${workshopId}/active-boats-list`)
  return data
}

export async function getAllBoatWorkCases(workshopId: number, boatId: number) {
  const { data } = await axios.get(`api/workshops/${workshopId}/boats/${boatId}/work-cases`)
  return data
}
export async function getWorkshopCompletedBoats(workshopId: EntityId, search: string, page: number, pageSize: number) {
  const { data } = await axios.get<IWorkshopBoatResponse[]>(`api/workshops/${workshopId}/completed-boats`, {
    params: { search, page, pageSize },
  })
  return data
}

export async function getWorkshopWorkOrderHistory(workshopId: EntityId) {
  const { data } = await axios.get(`api/workshops/${workshopId}/work-orders-history`)

  return data
}

export async function setWorkshopBoatStatus({
  status,
  workshopId,
  wbId,
  completeWorkOrders,
}: {
  status: WorkshopBoatStatus
  workshopId: EntityId
  wbId: EntityId
  completeWorkOrders?: boolean
}) {
  const { data } = await axios.post(
    `api/workshops/${workshopId}/boats/${wbId}/change-status`,
    { status },
    { params: { completeWorkOrders } },
  )

  return data
}

export async function getUserNotifications() {
  const { data } = await axios.get<INotification[]>('api/notifications')

  return data
}

export async function setNotificationStatus({ id, status }) {
  const { data } = await axios.post(`api/notifications/${id}/set-status`, { status })

  return data
}

export async function updateWorkshopBoat({ workshopId, wbId, form }) {
  const { data } = await axios.put(`api/workshops/${workshopId}/boats/${wbId}`, form)

  return data
}

export async function createOrUpdateWorkshopBoatSuggestions({ workshopId, wbId, form }) {
  const { data } = await axios.put(`api/workshops/${workshopId}/boats/${wbId}/suggestions`, form)
  return data
}

export async function assignWorkcase(workshopId: string, workcaseAssignment: IWorkcaseAssignmentRequest) {
  const { data } = await axios.post(
    `api/workshops/${workshopId}/work-cases/${workcaseAssignment.workcaseId}/assign`,
    workcaseAssignment,
  )
  return data
}

export async function updateWorkshopBoatResources({ workshopId, wbId, users }) {
  const { data } = await axios.put(`api/workshops/${workshopId}/boats/${wbId}/resources`, users)

  return data
}
export async function updateWorkshopBoatHours({ workshopId, wbId, userId, hours }) {
  const { data } = await axios.put(`api/workshops/${workshopId}/users/${userId}/boat/${wbId}/time?hours=${hours}`)

  return data
}

export async function uploadWorkshopBoatAttachment({ workshopId, wbId, formData }) {
  const { data } = await axios.post(`api/workshops/${workshopId}/boats/${wbId}/upload-attachment`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...authHeader(),
    },
  })

  return data
}

export async function deleteWorkshopBoatNote({ workshopId, wbId, noteId }) {
  const { data } = await axios.delete(`api/workshops/${workshopId}/boats/${wbId}/notes/${noteId}`)

  return data
}

export async function deleteWorkshopBoatSuggestion({ workshopId, wbId }) {
  const { data } = await axios.delete(`api/workshops/${workshopId}/boats/${wbId}/suggestions`)
  return data
}

export async function getWorkshopBoatSuggestion({ workshopId, wbId }) {
  const { data } = await axios.get(`api/workshops/${workshopId}/boats/${wbId}/suggestions`)
  return data
}

export async function deleteWorkshopBoatAttachment({ workshopId, wbId, attachmentId }) {
  const { data } = await axios.delete(`api/workshops/${workshopId}/boats/${wbId}/attachments/${attachmentId}`)
  return data
}

export async function getUserEmails() {
  const { data } = await axios.get<IUserEmailCategory[]>('api/user-emails')

  return data
}

export async function toggleUserEmail(emailTypeId: EntityId) {
  const { data } = await axios.post(`api/user-emails/${emailTypeId}/toggle`)

  return data
}

export async function getActiveSessions() {
  const { data } = await axios.get<IUserSession[]>('api/auth/user-sessions')

  return data
}

export async function login(form) {
  const { data } = await axios.post('api/auth/login', form)

  return data
}

export async function changePassword(form) {
  const { data } = await axios.post('api/auth/change-password', form)

  return data
}

export async function getBoatEquipmentDocuments(boatId: EntityId) {
  const { data } = await axios.get('api/document/GetAllDocumentsForBoatEquipment', { params: { boatId } })

  return data
}
export async function getBoatMaintenanceDocuments(boatId: EntityId) {
  const { data } = await axios.get('api/document/GetAllDocumentsForBoatMaintenance', { params: { boatId } })

  return data
}

export async function getFamiliarizedForms(boatId: EntityId): Promise<FamiliarizationForms[]> {
  const { data } = await axios.get('api/Crew/GetFamiliarizedFormsOnBoat', { params: { boatId } })
  return data
}

export async function getBoatDocuments(boatId: EntityId, folderId?: EntityId, term?: string) {
  const { data } = await axios.get<IBoatDocument[]>(`api/boats/${boatId}/documents`, {
    params: { folderId, term },
  })

  return data
}

export async function updateDocumentDashboardStatus({ documentId, showInDashboard }) {
  const { data } = await axios.patch(
    `api/document/UpdateDocumentDashboardStatus/${documentId}?showInDashboard=${showInDashboard}`,
  )

  return data
}

export async function getBoatDocumentFolders(boatId: EntityId, parentFolder?: EntityId, term?: string) {
  const { data } = await axios.get<IFileFolder[]>('api/document/GetDocumentFoldersForBoat', {
    params: { boatId, parentFolder, term },
  })

  return data
}

export async function createFileFolder(form) {
  const { data } = await axios.post('api/file/folders/create', form)

  return data
}

export async function updateFileFolder(id: EntityId, form) {
  const { data } = await axios.put(`api/file/folders/${id}`, form)

  return data
}

export async function deleteFileFolders(folderIds: number[]) {
  const { data } = await axios.delete('api/file/folders/bulk-delete', {
    params: { folderIds },
    paramsSerializer: qs.stringify,
  })

  return data
}

export async function assignFileToFolder({ fileId, folderId = undefined }) {
  const { data } = await axios.post(`api/file/${fileId}/assign-to-folder`, { folderId })

  return data
}

export async function getSupport() {
  const { data } = await axios.get('api/support/quick-guide', { responseType: 'blob' })
  return data
}
export async function getFileFolder(folderId) {
  const { data } = await axios.get<IFileFolder>(`api/file/folders/${folderId}`)

  return data
}

export async function canDeleteFolder(folderId) {
  const { data } = await axios.get<IFileFolder>(`api/file/folders/${folderId}/is-safe-to-delete`)

  return data
}

export async function getDocumentById(documentId: EntityId) {
  const { data } = await axios.get<IBoatDocument>(`api/Document/${documentId}`)

  return data
}

export async function getMainCategoriesForDocument(_key, documentId: EntityId): Promise<MainCategory[]> {
  const { data } = await axios.get(`api/MainCategory/ById?id=${documentId}`)

  return data
}
export async function searchForMainCategoriesForEquipment(_key, searchString): Promise<any[]> {
  const { data } = await axios.get(`api/MainCategory/Search?searchTerm=${searchString}`)

  return data
}
export async function fetchMainCategoriesTreeFromId(_key, mainCategoryId) {
  const { data } = await axios.get(
    `api/MainCategory/GetMainCategoryTreeByMainCategoryId?mainCategoryId=${mainCategoryId}`,
  )

  return data
}
export async function GetMainCategoryTreeByMainCategoryId(_key, mainCategoryId: EntityId): Promise<MainCategory[]> {
  const { data } = await axios.get(`api/MainCategory/ById?id=${mainCategoryId}`)

  return data
}
export async function GetAllBaseCategories(_key): Promise<MainCategory[]> {
  const { data } = await axios.get('api/MainCategory/GetAllBaseCategories')

  return data
}
export async function updateDocument(documentToUpdate: IBoatDocument) {
  const { data } = await axios.post('api/document/UpdateDocument', documentToUpdate)

  return data
}

export async function getCalendarMonthEvents(start: Date | string, end: Date | string, boatId?: EntityId) {
  const { data } = await axios.get<ICalendarEvent[]>('api/calendar', {
    params: { start, end, boatId },
  })

  return data
}

export async function getBoatCalendarServiceByType(
  boatId: EntityId,
  serviceType: ServiceRepeatType,
  token: string = undefined,
) {
  const { data } = await axios.get<any[]>('api/service/GetCalendarServicesByType', {
    params: { boatId, serviceType },
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function getBoatCardInfo(_): Promise<{ [key: number]: BoatCardInfo }> {
  const { data } = await axios.get('/api/boats/AllBoatCardInfoForClient222')

  return data
}

export async function checklistIsSafeToDelete(checklistId: EntityId) {
  const { data } = await axios.get<{ canDelete: boolean; reasons: string[] }>(
    `api/checklists/${checklistId}/can-delete`,
  )

  return data
}

export async function getExercises(): Promise<IExercise[]> {
  const { data } = await axios.get('api/exercises')

  return data
}

export async function getExercise(id: EntityId) {
  const { data } = await axios.get(`api/exercises/${id}`)

  return data
}

export async function createExercise(form: ExerciseFormType) {
  const { data } = await axios.post('api/exercises', form)

  return data
}

export async function updateExercise(id: EntityId, form) {
  const { data } = await axios.put(`api/exercises/${id}`, form)

  return data
}

export async function deleteExercise(id: EntityId) {
  const { data } = await axios.delete(`api/exercises/${id}`)

  return data
}

export async function createBoatExercise(boatId: EntityId, form: BoatExerciseForm) {
  const { data } = await axios.post(`api/boats/${boatId}/exercises`, form)

  return data
}

export async function updateBoatExercise(boatId: EntityId, exerciseId: EntityId, form: BoatExerciseForm) {
  const { data } = await axios.put(`api/boats/${boatId}/exercises/${exerciseId}`, form)

  return data
}

export async function getBoatExerciseOccurences(boatId: EntityId, start: Date, end: Date) {
  const { data } = await axios.get(`api/boats/${boatId}/exercises/occurences`, { params: { start, end } })

  return data
}

export async function getCompletedExerciseOccurrencesForBoat(boatId: EntityId): Promise<IExerciseOccurence[]> {
  const { data } = await axios.get(`api/boats/${boatId}/exercises/occurences/completed`)

  return data
}
export async function getNotCompletedExerciseOccurrencesForBoat(
  boatId: EntityId,
  start: Date | string,
  end: Date | string,
) {
  const { data } = await axios.get(`api/boats/${boatId}/exercises/occurences/not-completed`, { params: { start, end } })

  return data
}
export async function getCompletedExerciseOccurrencesForClient(): Promise<IExerciseOccurence[]> {
  const { data } = await axios.get('api/exercises/client/completed')

  return data
}

export async function getBoatExerciseOccurenceOverview(boatId: EntityId) {
  const { data } = await axios.get<IOccurenceOverview>(`api/boats/${boatId}/exercises/occurences-overview`)

  return data
}

export async function getBoatExercises(boatId: EntityId) {
  const { data } = await axios.get(`api/boats/${boatId}/exercises`)

  return data
}

export function deleteBoatExercise(boatId: EntityId, exerciseId: EntityId) {
  return axios.delete(`api/boats/${boatId}/exercises/${exerciseId}`)
}

export async function getBoatExercise(boatId: EntityId, exerciseId: EntityId) {
  const { data } = await axios.get(`api/boats/${boatId}/exercises/${exerciseId}`)

  return data
}

export async function createExerciseOccurence(boatId: EntityId, boatExerciseId: EntityId, form: any) {
  const { data } = await axios.post(`api/boats/${boatId}/exercises/${boatExerciseId}/occurences`, form)

  return data
}

export async function getExerciseDbOccurence(boatId: EntityId, boatExerciseId: EntityId, occurenceId: EntityId) {
  const { data } = await axios.get<IGetDbOccurenceResponse>(
    `api/boats/${boatId}/exercises/${boatExerciseId}/occurences/${occurenceId}`,
  )

  return data
}

export async function toggleOccurenceComplete(boatId: EntityId, boatExerciseId: EntityId, occurenceId: EntityId) {
  const { data } = await axios.post(
    `api/boats/${boatId}/exercises/${boatExerciseId}/occurences/${occurenceId}/toggle-complete`,
  )

  return data
}
export async function toggleOccurrenceNotDone(boatId: EntityId, boatExerciseId: EntityId, occurenceId: EntityId) {
  const { data } = await axios.post(
    `api/boats/${boatId}/exercises/${boatExerciseId}/occurences/${occurenceId}/not-done`,
  )

  return data
}

export async function getNotDoneExerciseOccurrencesForBoat(boatId: EntityId) {
  const { data } = await axios.get(`api/boats/${boatId}/exercises/occurences/not-done`)
  return data
}

export async function createOccurenceCrewMember(
  boatId: EntityId,
  boatExerciseId: EntityId,
  occurenceId: EntityId,
  crewId: EntityId,
) {
  const { data } = await axios.post(`api/boats/${boatId}/exercises/${boatExerciseId}/occurences/${occurenceId}/crew`, {
    userId: crewId,
  })

  return data
}
export async function createOccurenceNotes(
  notes: string,
  boatId: EntityId,
  boatExerciseId: EntityId,
  occurenceId: EntityId,
) {
  const { data } = await axios.post(`api/boats/${boatId}/exercises/${boatExerciseId}/occurences/${occurenceId}/notes`, {
    notes: notes,
  })

  return data
}

export async function updateOccurenceFiles(
  files: IFile[],
  boatId: EntityId,
  boatExerciseId: EntityId,
  occurenceId: EntityId,
) {
  const { data } = await axios.post(`api/boats/${boatId}/exercises/${boatExerciseId}/occurences/${occurenceId}/files`, {
    files: files,
  })

  return data
}

export async function deleteOccurenceCrewMember(
  boatId: EntityId,
  boatExerciseId: EntityId,
  occurenceId: EntityId,
  crewId: EntityId,
) {
  const { data } = await axios.delete(
    `api/boats/${boatId}/exercises/${boatExerciseId}/occurences/${occurenceId}/crew/${crewId}`,
  )

  return data
}

export async function getClientAccessGroups(clientId: EntityId) {
  const { data } = await axios.get(`api/client/GetAccessGroupsSelectForClient?clientId=${clientId}`)

  return data
}
export async function getUserAccessGroups(clientId: EntityId) {
  const { data } = await axios.get(`api/client/GetAccessGroupsSelectForUser?clientId=${clientId}`)

  return data
}

export async function createBoat(form: BoatFormType) {
  const { accessGroupId, addCommonConfigurations, commonConfigOpts, ...boat } = form
  const { data } = await axios.post('api/boats', { boat, accessGroupId, addCommonConfigurations, commonConfigOpts })

  return data
}

export async function createBarge(form: BoatFormType) {
  const { accessGroupId, ...boat } = form
  const { data } = await axios.post('api/boats/CreateBarge', { boat, accessGroupId })

  return data
}

export async function updateBoat(boatId: EntityId, form: BoatFormType) {
  const { accessGroupId, ...boat } = form
  const { data } = await axios.put(`api/boats/${boatId}`, boat)

  return data
}

export async function updateBarge(boatId: EntityId, form: BoatFormType) {
  const { accessGroupId, ...boat } = form
  const { data } = await axios.put(`api/boats/${boatId}/barge`, boat)

  return data
}

export async function getBoatTypes() {
  const { data } = await axios.get('api/boats/getboattypes')

  return data
}

export async function getBoatHullTypes() {
  const { data } = await axios.get('api/boats/GetHullTypes')

  return data
}

export async function getBoatTypeCategories() {
  const { data } = await axios.get('api/boats/getboattypecategories')

  return data
}

export async function getBoatTradeAres() {
  const { data } = await axios.get('api/boats/getboattradingareas')

  return data
}

export async function getClientDocumentTemplates(clientId: EntityId) {
  const { data } = await axios.get('api/documentTemplate/GetDocumentTemplatesForClient', { params: { clientId } })

  return data
}
export async function getBoatsDueDateOverview(key, clientId) {
  const { data } = await axios.get(`/api/Boats/GetBoatsDueDateOverview/?clientId=${clientId}`)

  return data
}
export async function getBoatSidebarStatistics(boatId) {
  const { data } = await axios.get(`/api/Boats/${boatId}/sidemenu-statistics`)

  return data
}
export async function getMainSidebarStatistics() {
  const { data } = await axios.get('/api/user/sidemenu-statistics')

  return data
}
export async function getUserBoatsDueDateOverview(key, clientId, clientAccessGroupId) {
  const { data } = await axios.get(
    `/api/Boats/GetUserBoatsDueDateOverview/?clientId=${clientId}&clientAccessGroupId=${clientAccessGroupId}`,
  )

  return data
}
export async function getBoatDueDateOverview(key, boatId) {
  const { data } = await axios.get(`/api/Boats/GetBoatDueDateOverview/?boatId=${boatId}`)

  return data
}
export async function getVesselDocumentsCloseToDueForBoat(key, boatId) {
  const { data } = await axios.get(`/api/documentTemplate/GetVesselDocumentsCloseToDue/${boatId}`)

  return data
}
export async function getDocumentsCloseToDueForBoat(key, boatId) {
  const { data } = await axios.get(`/api/boats/${boatId}/documents/close-to-due`)

  return data
}
export async function updateBoatStatus({ boatId, boatStatusId }) {
  const { data } = await axios.post(`api/boats/SetBoatStatus/${boatId}/${boatStatusId}`)

  return data
}

export async function updateCounterName(form) {
  const { data } = await axios.post('api/service/update-counter', form)

  return data
}

export async function updateCounterLogValue(boatCounterLogId: number, newValue: number) {
  const { data } = await axios.post(
    `api/service/UpdateBoatCounterLogValue?boatCounterLogId=${boatCounterLogId}&newValue=${newValue}`,
  )

  return data
}

export async function deleteCounterLogLine(boatCounterLogId: number) {
  const { data } = await axios.post(`api/service/DeleteBoatCounterLogLine?boatCounterLogId=${boatCounterLogId}`)

  return data
}

export async function getHavbruksloggenLocalities(clientId: EntityId): Promise<LocalityFromHavbruksloggen[]> {
  const { data } = await axios.get(`/api/HavbruksloggenIntegration/GetLocalitiesForClient/${clientId}`)

  return data
}

export async function updateHavbruksloggenLocalityForBoat(
  boatId: EntityId,
  boatLocationId: EntityId,
  newLocalityId: EntityId,
) {
  const { data } = await axios.put(`/api/boats/UpdateHavbruksloggenLocalityForBoat/${boatId}`, null, {
    params: { boatLocationId: boatLocationId, newLocalityId: newLocalityId },
  })

  return data
}
export async function createVesselOnLocality(boatId: EntityId, newLocalityId: EntityId) {
  const { data } = await axios.post(`/api/allocation/create-vessel/${boatId}`, null, {
    params: { newLocalityId: newLocalityId },
  })

  return data
}
export async function getBoatServicesForWorkshop(_, boatId: EntityId, equipmentId?: EntityId) {
  if (equipmentId) {
    // TODO: might need to test this
    const { data } = await axios.get(`/api/service/workshop/boat/${boatId}/${equipmentId}`)
    return data
  } else {
    const { data } = await axios.get(`/api/service/workshop/boat/${boatId}`)
    return data
  }
}
export async function getLogbookCategories(clientId) {
  const { data } = await axios.get(`api/clientAdmin/logbookCategory/${clientId}`)

  return data
}
export async function deleteLogbookConfiguration(logbookCategoryId) {
  const { data } = await axios.delete(`api/clientAdmin/logbookCategory/${logbookCategoryId}`)

  return data
}
export async function onCreateLogbookCategory(form) {
  const { data } = await axios.post('api/clientAdmin/logbookCategory/', form)

  return data
}
export async function deleteUserFromClient(clientId, userId, links) {
  const { data } = await axios.post(`api/clientAdmin/${clientId}/user/${userId}/delete`, links)

  return data
}
export async function renewInvitationForUser(userId) {
  const { data } = await axios.post(`api/clientAdmin/user/${userId}/renew`)

  return data
}
export async function getWeatherData(lat, lon) {
  const { data } = await axios.post('api/Weather/position', { latitude: lat, longitude: lon })

  return data
}
export async function getDocumentTemplatesForBoat(_, boatId) {
  const { data } = await axios.get(`api/documentTemplate/GetDocumentTemplatesForBoat?boatId=${boatId}`)

  return data
}
export async function getClientRefuelReport(form) {
  const { data } = await axios.post('api/report/client/refuel', form)

  return data
}
export async function getTagsForClient(clientId) {
  const { data } = await axios.get(`api/clientAdmin/${clientId}/tags`)

  return data
}
export async function getAdminDashboardOverview(key, clientId, from = null, to = null) {
  const { data } = await axios.get(`api/clientAdmin/DashboardOverview?clientId=${clientId}&from=${from}&to=${to}`)

  return data
}
export async function getUserDashboard(key, clientId, clientAccessGroupId = -2, from = null, to = null) {
  const { data } = await axios.get(
    `api/UserDashboard/GetUserDashboard?clientId=${clientId}&clientAccessGroupId=${clientAccessGroupId}&from=${from}&to=${to}`,
  )
  return data
}

export async function getUserDashboardMaintenanceOverview(key, clientId, accessGroup = -2, from = null, to = null) {
  const { data } = await axios.get(
    `api/UserDashboard/MaintenanceOverview?clientId=${clientId}&clientAccessGroupId=${accessGroup}&from=${from}&to=${to}`,
  )

  return data
}
export async function getUserDashboardMaintenanceChart(
  _key,
  clientId,
  clientAccessGroupId = -2,
  from = null,
  to = null,
) {
  const { data } = await axios.get(
    `api/UserDashboard/DashboardMaintenanceChart?clientId=${clientId}&clientAccessGroupId=${clientAccessGroupId}&from=${from}&to=${to}`,
  )

  return data
}
export async function getAdminDashboardMaintenanceOverview(key, clientId, from = null, to = null) {
  const { data } = await axios.get(
    `api/clientAdmin/DashboardMaintenanceOverview?clientId=${clientId}&from=${from}&to=${to}`,
  )

  return data
}
export async function getAdminDashboardMaintenanceChart(key, clientId, from = null, to = null) {
  const { data } = await axios.get(
    `api/clientAdmin/DashboardMaintenanceChart?clientId=${clientId}&from=${from}&to=${to}`,
  )

  return data
}
export async function getAdminDashboardDocumentChart(key, clientId, from = null, to = null) {
  const { data } = await axios.get(
    `api/clientAdmin/DashboardDocumentationChart?clientId=${clientId}&from=${from}&to=${to}`,
  )

  return data
}
export async function getUserDashboardDocumentChart(key, clientId, clientAccessGroupId = -2, from = null, to = null) {
  const { data } = await axios.get(
    `api/userDashboard/DashboardDocumentationChart?clientId=${clientId}&from=${from}&to=${to}&clientAccessGroupId=${clientAccessGroupId}`,
  )

  return data
}
export async function getUserDashboardDeviationChart(key, clientId, clientAccessGroupId = -2, from = null, to = null) {
  const { data } = await axios.get(
    `api/userDashboard/DashboardDeviationChart?clientId=${clientId}&from=${from}&to=${to}}&clientAccessGroupId=${clientAccessGroupId}`,
  )

  return data
}
export async function getAdminDashboardDeviationChart(key, clientId, from = null, to = null) {
  const { data } = await axios.get(`api/clientAdmin/DashboardDeviationChart?clientId=${clientId}&from=${from}&to=${to}`)

  return data
}
export async function onCreateTags(form) {
  const { data } = await axios.post('api/clientAdmin/tags/', form)

  return data
}
export async function deleteTag(tagId) {
  const { data } = await axios.delete(`api/clientAdmin/tags/${tagId}`)

  return data
}
export async function getAudits(count, page, userId, id, tableName) {
  const { data } = await axios.get<IAudit[]>('/api/audit/', {
    params: { count: count === 0 ? 10 : count, page: page, userId, id, tableName },
  })

  return data
}
export async function totalAudits(userId, id, tableName) {
  const { data } = await axios.get<number>('/api/audit/total', {
    params: { userId, id, tableName },
  })

  return data
}

export async function getBoatWorkshopWorkOrders(_key, boatId: EntityId): Promise<IWorkOrder[]> {
  const { data } = await axios.get(`/api/boats/${boatId}/workshop/work-orders`)

  return data
}
export async function getCompletedWorkCasesInWorkshop(_key, boatId: EntityId): Promise<IWorkshopBoat[]> {
  const { data } = await axios.get(`/api/boats/${boatId}/workshop/completed-boat-cases`)

  return data
}

export async function fetchFamiliarizationFormsForClient(_key, clientId: EntityId, showDeleted: boolean) {
  const { data } = await axios.get(`api/clientAdmin/GetFamiliarizationFormsForClient/${clientId}`, {
    params: { showDeleted },
  })

  return data
}

export async function getFamiliarizationApprovalSetting(_key, clientId: EntityId) {
  const key = 'FamiliarizationApproval'
  const { data } = await axios.get(`api/clientAdmin/GetClientSetting/${clientId}?key=${key}`)

  return data
}

export async function getClientSettingForUser(_key, clientId: EntityId, key: ClientSettingType) {
  const { data } = await axios.get(`api/Client/${clientId}/GetClientSetting?key=${key}`)

  return data
}
export async function getClientSetting({ clientId, key }: { clientId: EntityId; key: string }) {
  const { data } = await axios.get(`api/clientAdmin/GetClientSetting/${clientId}?key=${key}`)

  return data
}
export async function setClientSetting({ clientId, key }: { clientId: EntityId; key: string }) {
  const { data } = await axios.post(`api/clientAdmin/SetClientSetting/${clientId}?key=${key}`)

  return data
}
export async function setFamiliarizationApprovalSetting(clientId: EntityId) {
  const key = 'FamiliarizationApproval'
  const { data } = await axios.post(`api/clientAdmin/SetClientSetting/${clientId}?key=${key}`)

  return data
}
export async function getNeedsApproval(
  clientId: EntityId,
  familiarizationId: EntityId,
): Promise<FamiliarizationUserLink[]> {
  const { data } = await axios.get(`api/clientAdmin/FamiliarizationNeedsApproval/${clientId}/${familiarizationId}`)

  return data
}
export async function getNeedsApprovalSingle(famId: EntityId, showDeleted: boolean) {
  const { data } = await axios.get('api/Crew/FamiliarizationNeedsApprovalSingle', {
    params: { famId, showDeleted },
  })

  return data
}
export async function getNeedsApprovalCount(_key, famId: EntityId): Promise<number> {
  const { data } = await axios.get('api/Crew/FamiliarizationNeedsApprovalCount', {
    params: { famId },
  })

  return data
}

export async function approveFamiliarizationForm(fammiliarizationUserLinkId: number) {
  const { data } = await axios.post(`api/clientAdmin/ApproveFamiliarization/${fammiliarizationUserLinkId}`)

  return data
}
export async function approveFamiliarizationFormUser(fammiliarizationUserLinkId: number) {
  const { data } = await axios.post(`api/Crew/ApproveFamiliarization/${fammiliarizationUserLinkId}`)

  return data
}
export async function disapproveFamiliarizationForm(fammiliarizationUserLinkId: number) {
  const { data } = await axios.post(`api/clientAdmin/DisapproveFamiliarization/${fammiliarizationUserLinkId}`)

  return data
}
export async function updateFamiliarizationForm(familiarizationToUpdate) {
  const { data } = await axios.post('api/clientAdmin/UpdateFamiliarizationForm', familiarizationToUpdate)

  return data
}
export async function addNewFamiliarizationForm(familiarizationToAdd) {
  const { data } = await axios.post('api/clientAdmin/AddNewFamiliarizationForm', familiarizationToAdd)

  return data
}
export async function fetchBoatsForFamiliarizationFormClients(_key, familiarizationId: EntityId): Promise<IBoat[]> {
  const { data } = await axios.get(
    `api/clientAdmin/GetBoatsForFamiliarizationFormClients/?familiarizationId=${familiarizationId}`,
  )

  return data
}
export async function addBoatsToFamiliarizationForm(familiarizationFormId, boatsToAdd) {
  const { data } = await axios.post(
    `api/clientAdmin/AddBoatsToFamiliarizationForm/${familiarizationFormId}`,
    boatsToAdd,
  )

  return data
}

export async function getDisposedStockProductsByStockId(stockId: number) {
  const { data } = await axios.get(`api/stocks/${stockId}/disposed-products`)

  return data
}

export async function getDisposedStockEquipment(stockId: number) {
  const { data } = await axios.get(`api/stocks/${stockId}/disposed-equipment`)
  return data
}
export async function restoreStockProduct(stockProductId) {
  const { data } = await axios.put(`api/stocks/restore-product/${stockProductId}`)
  return data
}
export async function getEquipmentTemplateVesselUsage(equipmentId) {
  const { data } = await axios.get(`api/EquipmentTemplate/usedOnVessel/${equipmentId}`)

  return data
}
export async function deleteEquipment(equipmentId) {
  const { data } = await axios.post(`api/Equipment/DeleteEquipment/${equipmentId}`)

  return data
}
export async function createTemplateBasedOnEquimentRequest({ equipmentId, clientId }) {
  const { data } = await axios.post(`api/EquipmentTemplate/CreateEquipmentTemplate/${equipmentId}/${clientId}`)

  return data
}
export async function CreateEquipmentTemplate({ clientId, equipmentTemplate }) {
  const { data } = await axios.post(
    `api/EquipmentTemplate/CreateEquipmentTemplate?clientId=${clientId}`,
    equipmentTemplate,
  )
  return data
}
export async function setOperationsManagerForClientAccessGroup({
  clientAccessGroupId,
  userId,
  removeFromBoats = true, // bør vel vær satt til true default(????)
}) {
  const params = {
    clientAccessGroupId,
    userId,
    removeFromBoats,
  }
  const { data } = await axios.post('/api/boats/client-access-groups/operations-managers', params)
  return data
}

export async function getOperationsManagersForClientAccessGroup(clientAccessGroupId) {
  const { data } = await axios.get(`/api/boats/client-access-groups/${clientAccessGroupId}/operations-managers`)
  return data
}

export async function removeOperationsManagerFromClientAccessGroup({ clientAccessGroupId, userId }) {
  const { data } = await axios.delete(
    `/api/boats/client-access-groups/${clientAccessGroupId}/operations-managers/${userId}`,
  )
  return data
}

export async function setOperationsManagersForBoat({ boatId, userId }) {
  const params = { boatId, userId }
  const { data } = await axios.post('/api/boats/operations-managers', params)
  return data
}

export async function getOperationsManagersFromBoat(boatId) {
  const { data } = await axios.get(`/api/boats/${boatId}/operations-managers`)
  return data
}
export async function fetchAvailableUsersForBoat(clientId, boatId) {
  try {
    const users = await getBoatUsers(null, boatId, null)

    return users.map(user => ({
      userId: user.userId,
      name: user.name,
      email: user.email,
    }))
  } catch (error) {
    console.error('Error fetching available users for boat:', error)
    throw error
  }
}

export async function removeOperationsManagerFromBoat({ boatId, userId }) {
  const { data } = await axios.delete(`/api/boats/${boatId}/operations-managers/${userId}`)
  return data
}

export async function getAccessGroupsForBoat(boatId) {
  const { data } = await axios.get(`/api/boats/${boatId}/access-groups`)
  return data
}

export async function getAllOperationsManagers() {
  const { data } = await axios.get('/api/operations-managers')
  return data
}

export async function getWorkshopWorkOrderDetailsByWorkShopId(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopWorkOrderStatus>(`api/workshops/${workshopId}/workshop-workorder-details`)
  return data
}

export async function getAllUnassignedWorkshopWorkOrders(workshopId: EntityId) {
  const { data } = await axios.get<IWorkshopWorkOrders[]>(`api/workshops/${workshopId}/unassigned-workorders`)
  return data
}

export async function createNewWorkCaseFromWorkOrders({ workshopId, workshopWorkOrders }) {
  const { data } = await axios.post(`api/workshops/${workshopId}/workcases`, workshopWorkOrders)
  return data
}
export async function getAllocationsForClient(clientId) {
  const { data } = await axios.get(`api/Allocation/${clientId}`)

  return data
}
export async function fetchAdminClientsForUser(admin: boolean) {
  const { data } = await axios.get(`api/client/GetClientsForUser/?adminLinks=${admin}`)

  return data
}
export async function setLanguage(language) {
  const { data } = await axios.post(`api/user/set-language/${language}`)

  return data
}
export async function fetchDocumentCategories() {
  const { data } = await axios.get('api/document/GetDocumentCategories/')

  return data
}
export async function fetchDocumentTemplatesForUser() {
  const { data } = await axios.get('api/documentTemplate/GetDocumentTemplatesForUser/')

  return data
}
export async function updateDocumentTemplate(documentTemplate) {
  const { data } = await axios.post('api/documentTemplate/UpdateTemplate', documentTemplate)

  return data
}
export async function createDocumentTemplate(documentTemplate) {
  const { data } = await axios.post('api/documentTemplate/CreateTemplate', documentTemplate)

  return data
}
export async function SendDocumentByEmail(documentId: EntityId, email: string) {
  const { data } = await axios.post('api/document/send-by-email', {
    documentId,
    email,
  })

  return data
}

export async function getWorkshopUsersForDropdown(id) {
  const { data } = await axios.get(`api/workshops/${id}/available-users`)
  return data
}
export async function getAllStockUsersById(stockId: EntityId) {
  const { data } = await axios.get<IStockUser[]>(`api/stocks/${stockId}/users`)
  return data
}

export async function getRolesByCategoryId(categoryId: EntityId) {
  const { data } = await axios.get<Role[]>(`api/role/categories/${categoryId}`)
  return data
}

export async function addUserToStock({ id, form }) {
  const { data } = await axios.post(`api/stocks/${id}/users`, form)

  return data
}
export async function deleteStockShopUser({ stockId, id }) {
  const { data } = await axios.delete(`api/stocks/${stockId}/users/${id}`)

  return data
}

export async function getClientUserLink(id: EntityId) {
  const { data } = await axios.get<IClientUserLink>(`api/clientAdmin/clientuserlinks/${id}`)
  return data
}

export async function updateValidUntillAndNote(values) {
  const { id, clientUserLink } = values
  const { data } = await axios.put(`api/clientAdmin/clientuserlinks/${id}/update-validUntil-and-note`, clientUserLink)
  return data
}
export async function getUserVesselGroups() {
  const { data } = await axios.get('/api/boats/user-vessel-groups')

  return data
}
export async function getBoatsForClient(clientId: EntityId) {
  const { data } = await axios.get<IBoatOverviewDto[]>(`api/client/${clientId}/boats`)

  return data
}
export async function getMyWork() {
  const { data } = await axios.get('api/user/my-work')

  return data
}
export async function createReleaseNote(form) {
  const { data } = await axios.post('api/support/changelog', form)

  return data
}
export async function updateReleaseNote(form) {
  const { data } = await axios.put('api/support/changelog', form)

  return data
}
export async function deleteReleaseNote(id) {
  const { data } = await axios.delete(`api/support/changelog/${id}`)

  return data
}
export async function getMaintenanceTags(clientId: number) {
  const { data } = await axios.get('/api/tags/maintenance-tags', {
    params: { clientId },
  })

  return data
}
export async function updateMaintenanceTag(serviceId: number, tagId: number): Promise<any> {
  const { data } = await axios.put('/api/tags/maintenance-tags', { serviceId, tagId })
  return data
}
export async function updateTag(form): Promise<any> {
  const { data } = await axios.put('/api/clientAdmin/tags', form)
  return data
}

export async function getDefibrillatorData(boatId: number): Promise<Defibrillator[]> {
  const { data } = await axios.get(`/api/Equipment/${boatId}/GetDefibrillatorData`)
  return data
}

export async function getCommonProductCategories() {
  const { data } = await axios.get<IProductCategory[]>('/api/product-categories/common-lubricant-categories')
  return data
}

export async function getLubricantsByCategory(clientid: number, categoryId: number) {
  const { data } = await axios.get<IProduct[]>(`/api/client/${clientid}/product-categories/${categoryId}/products`)
  return data
}

export async function addLubricantMapPerEquipment({ equipmentId, form }) {
  const { data } = await axios.post(`api/equipment/${equipmentId}/lubricant-maps`, form)
  return data
}

export async function getLubricantMapPerEquipment(equipmentId) {
  const { data } = await axios.get<ILubricantMap[]>(`api/equipment/${equipmentId}/lubricant-maps`)
  return data
}

export async function removeLubricantFromEquipment(mainLubricantId) {
  const { data } = await axios.delete(`api/lubricant-maps/${mainLubricantId}`)
  return data
}

export async function updateLubricantMapPerEquipment({ equipmentId, equipmentLubricantMappingId, form }) {
  const { data } = await axios.put(
    `api/equipment/${equipmentId}/lubricant-maps/${equipmentLubricantMappingId}/alternatives`,
    form,
  )
  return data
}

export async function getLubricantMapPerBoat(boatId) {
  const { data } = await axios.get<ILubricantMap[]>(`api/boats/${boatId}/lubricant-maps`)
  return data
}

export async function getBoatWorkCaseInfo(workcaseId: number) {
  const { data } = await axios.get(`/api/work-cases/${workcaseId}`)

  return data
}

export async function validateRegistrationToken(token): Promise<IUser> {
  const { data } = await axios.get(`/api/auth/validateRegistrationToken?token=${token}`, requestHeader(false))
  return data
}
export async function completeRegistration(userForSaving): Promise<IUser> {
  const { data } = await axios.post('/api/auth/completeRegistration', userForSaving, requestHeader(false))

  return data
}
export async function forgotPassword(): Promise<IUser> {
  const { data } = await axios.get('/api/auth/ForgotPassword')

  return data
}
export async function getVesselType(boatId, token = undefined): Promise<string> {
  const { data } = await axios.get(`api/boats/${boatId}/vessel-type`, {
    headers: getShareTokenAuthHeader(token),
  })

  return data
}

export async function createBoatStorage(boatId) {
  const { data } = await axios.post(`/api/boats/${boatId}/stocks`)
  return data
}

export async function getPaginatedWorkshopOpenOrders({ pageParam = null, queryKey }) {
  const [_key, workshopId, searchKeyword] = queryKey

  const { data } = await axios.get<IWorkCasesPaginatedResponse>(`api/workshops/${workshopId}/open-orders`, {
    params: {
      cursor: pageParam,
      pageSize: 10,
      searchKeyword: searchKeyword,
      includeTotalCount: !pageParam && !searchKeyword,
    },
  })
  return data
}

export async function getWorkshopUserWithEvents(workshopId: number) {
  const { data } = await axios.get<IWorkshopUserDetails[]>(`api/workshops/${workshopId}/users-details`)
  return data
}

export async function getWorkshopUserDailyWorkCases(workshopId: EntityId, userId: EntityId, date: Date) {
  const endpointUrl = `api/workshops/${workshopId}/users/${userId}/work-cases/${date}`
  const { data } = await axios.get<IWorkshopWorkOrderSectionResponse[]>(endpointUrl)

  return data
}

export async function getOverDueWorkCasesByUser(workshopId: EntityId, userId: EntityId) {
  const endpointUrl = `api/workshops/${workshopId}/users/${userId}/overdue-workcases`
  const { data } = await axios.get<IWorkshopWorkOrderSectionResponse[]>(endpointUrl)

  return data
}

export async function updateWorkcaseStatus({ id, form }) {
  const { data } = await axios.patch(`api/work-cases/${id}/status`, form)
  return data
}

export async function getHasActiveCaptainForShutdown(boatId, boatShutdownRoutineId, token) {
  const { data } = await axios.get(
    `api/boats/${boatId}/startup-routines/hasActiveCaptainForShutdown?boatShutdownRoutineId=${boatShutdownRoutineId}`,
    {
      headers: getShareTokenAuthHeader(token),
    },
  )
  return data
}

export async function getCountersForEquipment(equipmentId) {
  const { data } = await axios.get(`/api/equipment/${equipmentId}/counters`)
  return data
}

export async function getLogsForCounterEquipment(counterId) {
  const { data } = await axios.get(`/api/service/GetLogsForCounterEquipment/${counterId}`)
  return data
}

export async function getPaginatedMyWorkCases({ pageParam = null, queryKey }) {
  const [_key, id, searchKeyword] = queryKey

  const { data } = await axios.get<IWorkCasesPaginatedResponse>(`api/workshops/${id}/my-workcases`, {
    params: {
      cursor: pageParam,
      pageSize: 10,
      searchKeyword: searchKeyword,
      includeTotalCount: !pageParam && !searchKeyword,
    },
  })
  return data
}
